import React from "react";
import { api, API, LOGO_URL_TRANSPARENT } from "@/lib/config";
import { Phone, Mail, MapPin, Download, MessageSquare, QrCode, Copy, Check, Printer } from "lucide-react";
import { toast } from "sonner";

const DEFAULTS = {
  company_name: "JFeva Solutions, LLC",
  owner_name: "Corwin Calloway",
  owner_title: "Owner",
  tagline: "Flooring & Tile · Florida",
  motto: "Our quality will floor you.",
  phone: "850-252-0162",
  email: "info@jfeva.com",
  service_area: "Florida & surrounding areas",
  website: "",
};

export default function BusinessCard() {
  const [info, setInfo] = React.useState(DEFAULTS);
  const [copied, setCopied] = React.useState(false);
  const cardUrl = typeof window !== "undefined" ? `${window.location.origin}/card` : "/card";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(cardUrl)}&bgcolor=fdfbf7&color=1a1a1a`;

  React.useEffect(() => {
    api.get("/card-settings")
      .then((r) => setInfo({ ...DEFAULTS, ...r.data }))
      .catch(() => {});
  }, []);

  const phoneRaw = "1" + (info.phone || "").replace(/\D/g, "").slice(-10);

  const smsBody = encodeURIComponent(
    `${info.company_name} — ${info.tagline}\n"${info.motto}"\n${info.phone}\n${info.email}\nSave contact & see gallery: ${cardUrl}`
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div data-testid="card-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="max-w-2xl">
        <div className="eyebrow text-[color:var(--brand-wood)]">Business Card</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          Save us to your contacts.
        </h1>
        <p className="mt-4 text-[color:var(--brand-woodDark)]">
          Tap once to save {info.company_name} to your phone, send us as a text, or print physical cards for your truck.
        </p>
      </div>

      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        {/* Card visual */}
        <div className="lg:col-span-7">
          <div className="mx-auto max-w-md">
            <div
              className="rounded-2xl bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] p-8 aspect-[7/4] shadow-2xl relative overflow-hidden"
              data-testid="card-visual"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 85% 15%, rgba(140,113,90,0.25), transparent 45%), radial-gradient(circle at 15% 85%, rgba(74,93,82,0.18), transparent 50%), url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85'/><feColorMatrix values='0 0 0 0 0.55 0 0 0 0 0.44 0 0 0 0 0.35 0 0 0 0.35 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                backgroundBlendMode: "screen, screen, overlay",
              }}
            >
              <div className="absolute inset-4 border border-white/15 rounded-xl pointer-events-none"/>
              <div className="relative h-full flex flex-col">
                <div className="flex items-start gap-5">
                  <img src={LOGO_URL_TRANSPARENT} alt={info.company_name} className="h-24 w-24 object-contain drop-shadow-[0_4px_16px_rgba(140,113,90,0.35)]"/>
                  <div className="pt-1 min-w-0 flex-1">
                    <div data-testid="card-company" className="font-display text-2xl leading-tight break-words">{info.company_name}</div>
                    <div data-testid="card-tagline" className="eyebrow text-[color:var(--brand-wood)] mt-2 truncate">{info.tagline}</div>
                    <div data-testid="card-owner" className="mt-3 text-sm font-medium text-white truncate">{info.owner_name}</div>
                    <div data-testid="card-owner-title" className="text-[10px] tracking-[0.2em] uppercase text-white/60 truncate">{info.owner_title}</div>
                  </div>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <p data-testid="card-motto" className="font-display italic text-lg text-white/95 text-center drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]">&ldquo;{info.motto}&rdquo;</p>
                </div>
                <div className="flex items-end justify-between text-xs">
                  <div>
                    <div data-testid="card-phone" className="font-medium">{info.phone}</div>
                    <div data-testid="card-email" className="opacity-85">{info.email}</div>
                  </div>
                  <div data-testid="card-area" className="text-right opacity-75 text-[10px] leading-tight max-w-[45%]">
                    {info.service_area}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact rows */}
            <div className="mt-6 rounded-2xl border border-black/10 bg-white overflow-hidden divide-y divide-black/5">
              <a href={`tel:${phoneRaw}`} data-testid="card-tap-phone" className="flex items-center gap-4 p-4 hover:bg-black/5 transition-colors">
                <span className="h-9 w-9 rounded-full bg-[color:var(--brand-stone)] text-[color:var(--brand-woodDark)] flex items-center justify-center"><Phone className="h-4 w-4"/></span>
                <div className="flex-1"><div className="text-sm font-medium">{info.phone}</div><div className="text-xs text-[color:var(--brand-woodDark)]">Tap to call</div></div>
              </a>
              <a href={`mailto:${info.email}`} data-testid="card-tap-email" className="flex items-center gap-4 p-4 hover:bg-black/5 transition-colors">
                <span className="h-9 w-9 rounded-full bg-[color:var(--brand-stone)] text-[color:var(--brand-woodDark)] flex items-center justify-center"><Mail className="h-4 w-4"/></span>
                <div className="flex-1"><div className="text-sm font-medium">{info.email}</div><div className="text-xs text-[color:var(--brand-woodDark)]">Tap to email</div></div>
              </a>
              <div className="flex items-center gap-4 p-4">
                <span className="h-9 w-9 rounded-full bg-[color:var(--brand-stone)] text-[color:var(--brand-woodDark)] flex items-center justify-center"><MapPin className="h-4 w-4"/></span>
                <div className="flex-1"><div className="text-sm font-medium">{info.service_area}</div><div className="text-xs text-[color:var(--brand-woodDark)]">Service area</div></div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="lg:col-span-5 space-y-3">
          <a
            href={`${API}/vcard`}
            data-testid="save-contact-btn"
            className="w-full inline-flex items-center justify-between gap-3 rounded-2xl bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-6 py-5 hover:-translate-y-0.5 transition-transform"
          >
            <div className="text-left">
              <div className="text-sm font-medium">Save to contacts</div>
              <div className="text-xs opacity-70">Downloads a .vcf card for your phone</div>
            </div>
            <Download className="h-5 w-5"/>
          </a>

          <a
            href={`sms:?&body=${smsBody}`}
            data-testid="send-sms-btn"
            className="w-full inline-flex items-center justify-between gap-3 rounded-2xl bg-white border border-black/10 px-6 py-5 hover:bg-[color:var(--brand-stone)]/40 transition-colors"
          >
            <div className="text-left">
              <div className="text-sm font-medium">Send via text</div>
              <div className="text-xs text-[color:var(--brand-woodDark)]">Opens Messages with contact + link</div>
            </div>
            <MessageSquare className="h-5 w-5 text-[color:var(--brand-woodDark)]"/>
          </a>

          <a
            href={`${API}/business-card/pdf?layout=single`}
            target="_blank" rel="noreferrer"
            data-testid="print-single-btn"
            className="w-full inline-flex items-center justify-between gap-3 rounded-2xl bg-white border border-black/10 px-6 py-5 hover:bg-[color:var(--brand-stone)]/40 transition-colors"
          >
            <div className="text-left">
              <div className="text-sm font-medium">Printable card (single, 3.5&quot; × 2&quot;)</div>
              <div className="text-xs text-[color:var(--brand-woodDark)]">Front + back PDF at exact card size</div>
            </div>
            <Printer className="h-5 w-5 text-[color:var(--brand-woodDark)]"/>
          </a>

          <a
            href={`${API}/business-card/pdf?layout=sheet`}
            target="_blank" rel="noreferrer"
            data-testid="print-sheet-btn"
            className="w-full inline-flex items-center justify-between gap-3 rounded-2xl bg-white border border-black/10 px-6 py-5 hover:bg-[color:var(--brand-stone)]/40 transition-colors"
          >
            <div className="text-left">
              <div className="text-sm font-medium">Printable sheet (10 per page)</div>
              <div className="text-xs text-[color:var(--brand-woodDark)]">US Letter, Avery-5371 compatible</div>
            </div>
            <Printer className="h-5 w-5 text-[color:var(--brand-woodDark)]"/>
          </a>

          <button
            onClick={copyLink}
            data-testid="copy-link-btn"
            className="w-full inline-flex items-center justify-between gap-3 rounded-2xl bg-white border border-black/10 px-6 py-5 hover:bg-[color:var(--brand-stone)]/40 transition-colors"
          >
            <div className="text-left">
              <div className="text-sm font-medium">{copied ? "Link copied" : "Copy shareable link"}</div>
              <div className="text-xs text-[color:var(--brand-woodDark)] truncate max-w-[220px]">{cardUrl}</div>
            </div>
            {copied ? <Check className="h-5 w-5 text-emerald-600"/> : <Copy className="h-5 w-5 text-[color:var(--brand-woodDark)]"/>}
          </button>

          <div className="rounded-2xl border border-black/10 bg-white p-6 flex items-center gap-5">
            <img src={qrUrl} alt="Scan for Jfeva contact" data-testid="card-qr" className="h-28 w-28 rounded-lg"/>
            <div>
              <div className="text-sm font-medium flex items-center gap-2"><QrCode className="h-4 w-4"/> Scan to share</div>
              <div className="text-xs text-[color:var(--brand-woodDark)] mt-1">Point a camera at this code to open the card on any phone.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
